/**
 * etRekenmachine — rekenkern voor de examentraining-rekenmachine.
 *
 * Pure module (geen imports), zoals examentrainingScoring.js. De widget erboven
 * (EtRekenmachine.js) is alleen knoppen; alle rekenlogica en formattering zit
 * hier, zodat het testbaar is en het hoofdplatform het kan hergebruiken.
 *
 * Gedrag gespiegeld aan de examenrekenmachine die leerlingen kennen:
 *  - komma als decimaalteken (invoer én uitvoer)
 *  - graden voor sin/cos/tan (examencontext), INV voor de inverse
 *  - ×10^y, x², x^y, x⁻¹, √, π, haakjes, (−) voor negatief
 *  - ANS = vorige uitkomst
 *  - f↔d: uitkomst als breuk ↔ decimaal
 */

// ── tokenizer ───────────────────────────────────────────────────────────────
// Ondersteunde tokens: getallen (komma of punt), + - * / ^ ( ), functienamen.
function tokenize(expr) {
  const tokens = [];
  let i = 0;
  const s = expr.replace(/,/g, '.').replace(/×/g, '*').replace(/÷/g, '/').replace(/−/g, '-');
  while (i < s.length) {
    const c = s[i];
    if (c === ' ') { i++; continue; }
    if (/[0-9.]/.test(c)) {
      let j = i;
      while (j < s.length && /[0-9.]/.test(s[j])) j++;
      const num = s.slice(i, j);
      if ((num.match(/\./g) || []).length > 1) throw new Error('ongeldig getal');
      tokens.push({ t: 'num', v: parseFloat(num) });
      i = j; continue;
    }
    if (/[a-zπ]/i.test(c)) {
      let j = i;
      while (j < s.length && /[a-zπ]/i.test(s[j])) j++;
      tokens.push({ t: 'fn', v: s.slice(i, j).toLowerCase() });
      i = j; continue;
    }
    if ('+-*/^()'.includes(c)) { tokens.push({ t: c }); i++; continue; }
    throw new Error(`onbekend teken: ${c}`);
  }
  return tokens;
}

// ── parser/evaluator (recursive descent, juiste voorrang) ───────────────────
const GRADEN = Math.PI / 180;
const FN = {
  sin: (x) => Math.sin(x * GRADEN),
  cos: (x) => Math.cos(x * GRADEN),
  tan: (x) => Math.tan(x * GRADEN),
  asin: (x) => Math.asin(x) / GRADEN,
  acos: (x) => Math.acos(x) / GRADEN,
  atan: (x) => Math.atan(x) / GRADEN,
  sqrt: (x) => { if (x < 0) throw new Error('wortel van negatief'); return Math.sqrt(x); },
};

function parse(tokens, ansWaarde) {
  let pos = 0;
  const peek = () => tokens[pos];
  const eat = () => tokens[pos++];

  function expressie() {           // + en -
    let links = term();
    while (peek() && (peek().t === '+' || peek().t === '-')) {
      const op = eat().t;
      const rechts = term();
      links = op === '+' ? links + rechts : links - rechts;
    }
    return links;
  }
  function term() {                // * en /
    let links = unair();
    while (peek() && (peek().t === '*' || peek().t === '/')) {
      const op = eat().t;
      const rechts = unair();
      if (op === '/') { if (rechts === 0) throw new Error('delen door nul'); links = links / rechts; }
      else links = links * rechts;
    }
    return links;
  }
  // Volgorde bewust: unair BOVEN macht, zodat -2^2 = -(2^2) = -4 — de conventie
  // die leerlingen op school en op de examenrekenmachine leren (review-fix #1).
  // De exponent zelf loopt via unair, dus 2^-2 = 0,25 blijft werken.
  function unair() {
    if (peek() && peek().t === '-') { eat(); return -unair(); }
    if (peek() && peek().t === '+') { eat(); return unair(); }
    return macht();
  }
  function macht() {               // ^ (rechts-associatief)
    const basis = primair();
    if (peek() && peek().t === '^') { eat(); return Math.pow(basis, unair()); }
    return basis;
  }
  function primair() {
    const tok = eat();
    if (!tok) throw new Error('onvolledige berekening');
    if (tok.t === 'num') return tok.v;
    if (tok.t === '(') {
      const v = expressie();
      if (!peek() || eat().t !== ')') throw new Error('haakje niet gesloten');
      return v;
    }
    if (tok.t === 'fn') {
      if (tok.v === 'pi' || tok.v === 'π') return Math.PI;
      if (tok.v === 'ans') return ansWaarde ?? 0;
      const f = FN[tok.v];
      if (!f) throw new Error(`onbekende functie: ${tok.v}`);
      if (!peek() || eat().t !== '(') throw new Error(`${tok.v} verwacht haakjes`);
      const arg = expressie();
      if (!peek() || eat().t !== ')') throw new Error('haakje niet gesloten');
      const uit = f(arg);
      if (!Number.isFinite(uit)) throw new Error('geen geldige uitkomst');
      return uit;
    }
    throw new Error('onverwacht teken');
  }

  const resultaat = expressie();
  if (pos < tokens.length) throw new Error('controleer je berekening');
  if (!Number.isFinite(resultaat)) throw new Error('geen geldige uitkomst');
  return resultaat;
}

/**
 * Reken een expressie uit. Komma's mogen; ANS wordt ingevuld.
 * @returns {number}  gooit Error met NL-boodschap bij ongeldige invoer
 */
// Impliciete vermenigvoudiging zoals op de examenrekenmachine: 2(3+4), 2π,
// 2sin(30), (1+2)(3+4) en ans2 krijgen een ×-teken ingevoegd (review-fix #5).
const NULARGE_FN = new Set(['pi', 'π', 'ans']);
function voegImplicieteMaalIn(tokens) {
  const uit = [];
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (uit.length > 0) {
      const vorig = uit[uit.length - 1];
      const vorigIsWaarde = vorig.t === 'num' || vorig.t === ')'
        || (vorig.t === 'fn' && NULARGE_FN.has(vorig.v));
      const ditBegintWaarde = tok.t === 'num' || tok.t === '(' || tok.t === 'fn';
      if (vorigIsWaarde && ditBegintWaarde) uit.push({ t: '*' });
    }
    uit.push(tok);
  }
  return uit;
}

export function reken(expr, ansWaarde = 0) {
  const tokens = voegImplicieteMaalIn(tokenize(String(expr)));
  if (tokens.length === 0) throw new Error('lege invoer');
  return parse(tokens, ansWaarde);
}

/** Toon een getal met komma, max 10 significante cijfers, zonder ruis-nullen. */
export function toonGetal(x) {
  if (!Number.isFinite(x)) return 'fout';
  let s = Math.abs(x) >= 1e10 || (Math.abs(x) < 1e-7 && x !== 0)
    ? x.toExponential(6)
    : String(Math.round(x * 1e9) / 1e9);
  return s.replace('.', ',');
}

/**
 * Benader een decimaal als breuk (kettingbreuk, noemer ≤ 1000).
 * @returns {{teller:number, noemer:number}|null} null als geen nette breuk bestaat
 */
export function alsBreuk(x) {
  if (!Number.isFinite(x)) return null;
  const negatief = x < 0; x = Math.abs(x);
  let [h1, h0, k1, k0] = [Math.floor(x), 1, 1, 0];
  let rest = x - Math.floor(x);
  while (rest > 1e-9 && k1 <= 1000) {
    const r = 1 / rest;
    const a = Math.floor(r);
    [h1, h0] = [a * h1 + h0, h1];
    [k1, k0] = [a * k1 + k0, k1];
    rest = r - a;
  }
  if (k1 > 1000 || k1 < 1) return null;
  if (Math.abs(h1 / k1 - x) > 1e-9) return null;
  return { teller: negatief ? -h1 : h1, noemer: k1 };
}
